import {
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { catchError, of, Subject, takeUntil } from 'rxjs';
import { DIALOG_BUTTONS } from 'src/app/shared/constants';
import { EmployeeProject } from 'src/app/shared/models';
import { AlertService, AlertType, AuthService, EmployeeService } from 'src/app/shared/services';
import { DATE_FORMATS } from 'src/app/shared/services/helpers/date.helper';
import { TimeLogController } from 'src/app/shared/services/time-log.controller';
import { TimeLogService } from 'src/app/shared/services/time-log.service';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-submit-report',
  templateUrl: './submit-report.component.html',
  styleUrls: ['./submit-report.component.scss'],
  imports: [
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
  ],
})
export class SubmitReportComponent implements OnInit, OnDestroy {
  projects = input<EmployeeProject[]>([]);
  private employeeService = inject(EmployeeService);
  private authService = inject(AuthService);
  private ctrl = inject(TimeLogController);
  private alertService = inject(AlertService);
  private timeLogService = inject(TimeLogService);
  translate = inject(TranslateService);

  readonly multipartFile = viewChild<ElementRef>('multipartFile');

  private destroy$ = new Subject<void>();
  public projectList = signal<EmployeeProject[]>([]);

  acceptedTypes = {
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };

  readonly buttons = DIALOG_BUTTONS;

  reportForm: FormGroup;
  private firstDateOfTheMonth = moment().add(-1, 'month').startOf('month');
  minDate = moment().add(-12, 'months').startOf('month');
  maxDate = moment().startOf('month');

  ngOnInit() {
    this.buildForm();
    this.initProjects();
  }

  buildForm(): void {
    this.reportForm = new FormGroup({
      project: new FormControl('', Validators.required),
      dateControl: new FormControl(this.firstDateOfTheMonth),
      multipartFile: new FormControl(null, Validators.required),
    });
  }

  async initProjects() {
    let projects: EmployeeProject[];
    if (this.projects()?.length) {
      projects = this.projects();
    } else {
      projects = await this.employeeService.loadMyProjects(this.authService.userId());
    }

    projects.sort((a, b) => (a.name.toLocaleUpperCase() > b.name.toLocaleUpperCase() ? 1 : -1));
    this.projectList.set(projects);

    if (this.projectList().length === 1) {
      this.reportForm.controls.project.setValue(this.projectList()[0].id);
    }
  }

  setMonthAndYear(normalizedMonthAndYear: moment.Moment, datepicker: MatDatepicker<moment.Moment>) {
    const ctrlValue = this.reportForm.controls.dateControl.value;
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    this.reportForm.controls.dateControl.setValue(ctrlValue);
    datepicker.close();
    this.ctrl.clearEntries();
  }

  onFileSelected(file: File): void {
    if (file == null) {
      this.reportForm.controls.multipartFile.markAsTouched();
    }
    this.reportForm.controls.multipartFile.setValue(file);
  }

  onSubmit(): void {
    if (this.reportForm.valid) {
      const fromDate = moment(this.reportForm.controls.dateControl.value)
        .startOf('month')
        .format('YYYY-MM-DD');
      const toDate = moment(this.reportForm.controls.dateControl.value)
        .endOf('month')
        .format('YYYY-MM-DD');
      const formData: FormData = new FormData();
      formData.append('fromDate', fromDate);
      formData.append('toDate', toDate);
      formData.append('file', new Blob([this.reportForm.controls.multipartFile.value]));

      this.timeLogService
        .submitReport(this.reportForm.controls.project.value, formData)
        .pipe(
          takeUntil(this.destroy$),
          catchError((err) => {
            return of([err]);
          }),
        )
        .subscribe((response) => {
          if (response[0].status == 200) {
            this.alertService.showAlert(response[0].error.text, AlertType.success);
            this.reportForm.controls.multipartFile.reset();
          } else {
            this.reportForm.controls.multipartFile.reset();
            this.alertService.showAlert(response[0].error, AlertType.error);
          }
        });
    }
  }

  displayFn(project: EmployeeProject): string {
    return project ? `${project.name}` : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
